import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  Icon,
  IconButton,
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  CalendarIcon,
  DownloadIcon,
  ExternalLinkIcon,
  RepeatIcon,
  SearchIcon,
  SmallCloseIcon,
} from '@chakra-ui/icons';

// @ts-ignore
import { DateRangePicker } from 'react-date-range';

import { useUrlFilters } from 'hooks/useUrlFilters';
import Card from 'components/card/Card';
import Paginate from 'components/paginate/Paginate';
import MultiSelect from 'components/fields/MultiSelect';
import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import { AuthContext } from 'contexts/AuthContext';
import {
  apiGetBaraReport,
  apiDownloadBaraReportCsv,
  apiGetBaraAccessibleBranches,
} from 'api';
import type {
  APIBaraReportRow,
  APIBaraReportResponse,
  APIBaraReportSummary,
  APIBaraBranch,
} from 'types/api.types';
import {
  ROLE_ADMIN,
  JIRA_BASE_URL,
  BARA_REFERRAL_STATUS_READY_FOR_REVIEW,
  BARA_REFERRAL_STATUS_UNDER_REVIEW,
  BARA_REFERRAL_STATUS_APPROVED,
  BARA_REFERRAL_STATUS_DECLINED,
  BARA_REFERRAL_STATUS_RECALLED,
  BARA_REFERRAL_STATUS_NEEDS_INFO,
} from 'variables/constants';
import getDateString from 'helpers/getDateString';

const STATUS_BADGE: Record<number, { label: string; color: string }> = {
  [BARA_REFERRAL_STATUS_READY_FOR_REVIEW]: {
    label: 'Ready for Review',
    color: 'orange',
  },
  [BARA_REFERRAL_STATUS_UNDER_REVIEW]: { label: 'Under Review', color: 'blue' },
  [BARA_REFERRAL_STATUS_APPROVED]: { label: 'Approved', color: 'green' },
  [BARA_REFERRAL_STATUS_DECLINED]: { label: 'Declined', color: 'red' },
  [BARA_REFERRAL_STATUS_RECALLED]: { label: 'Recalled', color: 'gray' },
  [BARA_REFERRAL_STATUS_NEEDS_INFO]: { label: 'Needs Info', color: 'yellow' },
};

const STATUS_FILTER_OPTIONS = [
  BARA_REFERRAL_STATUS_READY_FOR_REVIEW,
  BARA_REFERRAL_STATUS_UNDER_REVIEW,
  BARA_REFERRAL_STATUS_APPROVED,
  BARA_REFERRAL_STATUS_DECLINED,
  BARA_REFERRAL_STATUS_NEEDS_INFO,
];

const SIO_BADGE: Record<
  string,
  { label: string; color: string; hint: string }
> = {
  yes: { label: 'Yes', color: 'orange', hint: 'Flagged as SIO' },
  no: { label: 'No', color: 'gray', hint: 'Reviewed — not an SIO' },
};

const SIO_FILTER_OPTIONS = [
  { id: 1, name: 'Yes', value: 'yes' },
  { id: 2, name: 'No', value: 'no' },
  { id: 3, name: 'Not determined', value: 'unknown' },
];

// Committed filters live in the query string so a report view can be shared
// and survives a reload; the controls below hold uncommitted drafts until
// Search is pressed.
const REPORT_FILTER_KEYS = [
  'branch_id',
  'status',
  'sio',
  'start_date',
  'end_date',
] as const;

const parseIdList = (value: string): number[] =>
  value
    ? value
        .split(',')
        .map(Number)
        .filter((id) => !Number.isNaN(id))
    : [];

const parseSioIds = (value: string): number[] => {
  const values = value.split(',');
  return SIO_FILTER_OPTIONS.filter((o) => values.includes(o.value)).map(
    (o) => o.id,
  );
};

const serializeSio = (ids: number[]) =>
  SIO_FILTER_OPTIONS.filter((o) => ids.includes(o.id))
    .map((o) => o.value)
    .join(',');

// Parsed as local midnight so the picker shows the same day the URL names.
const parseDateParam = (value: string): Date | null =>
  value ? new Date(`${value}T00:00:00`) : null;

const FALLBACK_PAGE_SIZE = 50;

const REPORT_TIME_ZONE = 'America/Toronto';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-CA', { timeZone: REPORT_TIME_ZONE });

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  return new Date(value)
    .toLocaleString('en-CA', {
      timeZone: REPORT_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    })
    .replace(',', '');
};

const formatReviewedBy = (name: string, reviewedAt: string | null) =>
  reviewedAt ? `${name} (${formatDate(reviewedAt)})` : name;

const formatTurnaround = (minutes: number | null) => {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(minutes / 1440);
  const h = Math.round((minutes % 1440) / 60);
  return h ? `${d}d ${h}h` : `${d}d`;
};

const BaraReferralReportView = () => {
  const { user } = useContext(AuthContext) as { user: { role: number } };
  const isAdmin = user?.role === ROLE_ADMIN;
  const colorMode = useColorModeValue('light', 'dark');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const stickyBg = useColorModeValue('white', 'navy.800');
  const stickyBorder = useColorModeValue('gray.200', 'whiteAlpha.300');

  const stickyCellProps = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bg: stickyBg,
    borderRight: '1px solid',
    borderRightColor: stickyBorder,
  };

  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const [exporting, setExporting] = useState(false);

  // Applied filters — the query string is the single source of truth.
  const { setFilters, page, setPage, resetAll, searchParams } =
    useUrlFilters(REPORT_FILTER_KEYS);

  const branchParam = searchParams.get('branch_id') ?? '';
  const statusParam = searchParams.get('status') ?? '';
  const sioParam = searchParams.get('sio') ?? '';
  const startDateParam = searchParams.get('start_date') ?? '';
  const endDateParam = searchParams.get('end_date') ?? '';

  const appliedFilterCount =
    (isAdmin && branchParam ? 1 : 0) +
    (statusParam ? 1 : 0) +
    (sioParam ? 1 : 0) +
    (startDateParam || endDateParam ? 1 : 0);

  // Draft filters — edited freely, sent only when Search is pressed.
  const [branchDraft, setBranchDraft] = useState<number[]>(() =>
    parseIdList(branchParam),
  );
  const [statusDraft, setStatusDraft] = useState<number[]>(() =>
    parseIdList(statusParam),
  );
  const [sioDraft, setSioDraft] = useState<number[]>(() =>
    parseSioIds(sioParam),
  );
  const [periodDraft, setPeriodDraft] = useState<{
    startDate: Date | null;
    endDate: Date | null;
    key: string;
  }>(() => ({
    startDate: parseDateParam(startDateParam),
    endDate: parseDateParam(endDateParam),
    key: 'selection',
  }));

  // Keep the controls in step with the URL (back/forward, shared link).
  useEffect(() => {
    setBranchDraft(parseIdList(branchParam));
    setStatusDraft(parseIdList(statusParam));
    setSioDraft(parseSioIds(sioParam));
    setPeriodDraft({
      startDate: parseDateParam(startDateParam),
      endDate: parseDateParam(endDateParam),
      key: 'selection',
    });
  }, [branchParam, statusParam, sioParam, startDateParam, endDateParam]);

  const { data: branchData, isLoading: loadingBranches } = useQuery({
    queryKey: ['bara-accessible-branches'],
    queryFn: () =>
      apiGetBaraAccessibleBranches({ all: true, has_referrals: true }),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
  }) as { data: { results?: APIBaraBranch[] }; isLoading: boolean };

  const branches = useMemo(
    () => (Array.isArray(branchData?.results) ? branchData.results : []),
    [branchData],
  );

  const buildFilterParams = useCallback(() => {
    const params: Record<string, string> = {};
    REPORT_FILTER_KEYS.forEach((key) => {
      if (key === 'branch_id' && !isAdmin) return;
      const value = searchParams.get(key);
      if (value) params[key] = value;
    });
    return params;
  }, [isAdmin, searchParams]);

  const filterParams = useMemo(() => buildFilterParams(), [buildFilterParams]);

  // react-query owns the request, so returning to the tab refetches the
  // report with whatever filters the query string currently holds.
  const {
    data: report,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: ['bara-report', page, filterParams],
    queryFn: () =>
      apiGetBaraReport({ ...filterParams, page: String(page + 1) }),
  }) as {
    data: Partial<APIBaraReportResponse>;
    isLoading: boolean;
    isFetching: boolean;
    error: unknown;
    refetch: () => void;
  };

  const rows: APIBaraReportRow[] = report?.results ?? [];
  const summary: APIBaraReportSummary | null = report?.summary ?? null;
  const count = report?.count ?? 0;
  const pageSize = report?.page_size ?? FALLBACK_PAGE_SIZE;

  // Branch labels carry the referral count the report returned for each one.
  const branchOptions = useMemo(() => {
    const branchCounts = report?.branch_counts ?? {};
    return branches.map((b) => ({
      id: b.id,
      name: `${b.name} (${branchCounts[String(b.id)] ?? 0})`,
    }));
  }, [branches, report]);

  const statusOptions = useMemo(
    () =>
      STATUS_FILTER_OPTIONS.map((value) => ({
        id: value,
        name: STATUS_BADGE[value].label,
      })),
    [],
  );

  useEffect(() => {
    if (!error) return;
    toastRef.current({
      title: 'Failed to load report',
      status: 'error',
      duration: 3000,
    });
  }, [error]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await apiDownloadBaraReportCsv(buildFilterParams());
    } catch {
      toastRef.current({
        title: 'Failed to download report',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setExporting(false);
    }
  };

  // Commits the drafts to the URL, which is what sends the request.
  const handleSearch = () => {
    setFilters({
      branch_id: isAdmin ? branchDraft.join(',') : '',
      status: statusDraft.join(','),
      sio: serializeSio(sioDraft),
      start_date: periodDraft.startDate
        ? getDateString(periodDraft.startDate)
        : '',
      end_date: periodDraft.endDate ? getDateString(periodDraft.endDate) : '',
    });
  };

  const clearDraftDates = () => {
    setPeriodDraft({ startDate: null, endDate: null, key: 'selection' });
  };

  const resetFilters = () => {
    setBranchDraft([]);
    setStatusDraft([]);
    setSioDraft([]);
    clearDraftDates();
    resetAll();
  };

  const hasDraftDates = !!(periodDraft.startDate || periodDraft.endDate);

  const hasFilterInput =
    appliedFilterCount > 0 ||
    branchDraft.length > 0 ||
    statusDraft.length > 0 ||
    sioDraft.length > 0 ||
    hasDraftDates;

  const dateFilterLabel = hasDraftDates
    ? [
        periodDraft.startDate ? getDateString(periodDraft.startDate) : '…',
        periodDraft.endDate ? getDateString(periodDraft.endDate) : '…',
      ].join(' — ')
    : 'Any date';

  const tableHeaders = [
    'Broker',
    ...(isAdmin ? ['Branch'] : []),
    'Market',
    'Status',
    'Assigned To',
    'Jira',
    'SIO',
    'Post-Bind',
    'Post-Bind Reviewed By',
    'Submitted',
    'Turnaround',
  ];

  // The tiles always render so the table below them keeps its position; only
  // the number swaps for a spinner while a request is in flight. A value of
  // undefined means the report carried no summary for that tile.
  const summaryTiles: {
    label: string;
    value?: string | number;
    color: string;
  }[] = [
    { label: 'Total', value: summary?.total, color: 'gray' },
    { label: 'Approved', value: summary?.approved, color: 'green' },
    { label: 'Declined', value: summary?.declined, color: 'red' },
    { label: 'SIO', value: summary?.sio_count, color: 'orange' },
    {
      label: 'Post-Bind Reviewed',
      value: summary
        ? `${summary.post_bind_reviewed} / ${summary.approved}`
        : undefined,
      color: 'teal',
    },
    {
      label: 'Approval Rate',
      value: summary ? `${summary.approval_rate}%` : undefined,
      color: 'blue',
    },
  ];

  return (
    <BaraFeatureGuard>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} gap="20px" mb="20px">
        {summaryTiles.map(({ label, value, color }) => (
          <Card key={label} py="15px">
            <Text
              fontSize="10px"
              fontWeight="700"
              color="gray.400"
              textTransform="uppercase"
              letterSpacing="0.8px"
            >
              {label}
            </Text>
            <Text
              fontSize="22px"
              fontWeight="700"
              color={`${color}.500`}
              lineHeight="1.2"
              mt="6px"
            >
              {/* The spinner is inline-block, so it sits inside the same line
                  box the number would occupy and the card keeps its height. */}
              {value ?? (isFetching ? <Spinner size="sm" /> : '—')}
            </Text>
          </Card>
        ))}
      </SimpleGrid>

      <Card flexDirection="column" w="100%" px="0px">
        <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Broker After-Hours Referral Report
          </Text>
          <Flex gap={2} align="center">
            <Tooltip label="Refresh report" hasArrow>
              <IconButton
                aria-label="Refresh report"
                icon={<RepeatIcon />}
                onClick={() => refetch()}
                variant="ghost"
                isLoading={isFetching}
              />
            </Tooltip>
          </Flex>
        </Flex>

        <Box
          px="20px"
          pb="20px"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            {isAdmin && (
              <Box w={{ base: '100%', lg: '240px' }}>
                <FormLabel fontSize="sm" mb={1}>
                  Branch
                </FormLabel>
                <MultiSelect
                  selectedIds={branchDraft}
                  availableItems={branchOptions}
                  isLoading={loadingBranches}
                  onChange={setBranchDraft}
                  label="All branches"
                  itemLabelSingular="branch"
                  itemLabelPlural="branches"
                  showTags={false}
                />
              </Box>
            )}

            <Box w={{ base: '100%', lg: '220px' }}>
              <FormLabel fontSize="sm" mb={1}>
                Status
              </FormLabel>
              <MultiSelect
                selectedIds={statusDraft}
                availableItems={statusOptions}
                isLoading={false}
                onChange={setStatusDraft}
                label="All statuses"
                itemLabelSingular="status"
                itemLabelPlural="statuses"
                showTags={false}
              />
            </Box>

            <Box w={{ base: '100%', lg: '200px' }}>
              <FormLabel fontSize="sm" mb={1}>
                SIO
              </FormLabel>
              <MultiSelect
                selectedIds={sioDraft}
                availableItems={SIO_FILTER_OPTIONS}
                isLoading={false}
                onChange={setSioDraft}
                label="All SIO"
                itemLabelSingular="SIO state"
                itemLabelPlural="SIO states"
                showTags={false}
              />
            </Box>

            <Box>
              <FormLabel fontSize="sm" mb={1}>
                Period
              </FormLabel>
              <Popover placement="bottom-start">
                {({ onClose }) => (
                  <>
                    <PopoverTrigger>
                      <Button
                        size="md"
                        variant="outline"
                        fontWeight="normal"
                        leftIcon={<CalendarIcon />}
                        colorScheme={hasDraftDates ? 'blue' : 'gray'}
                      >
                        {dateFilterLabel}
                      </Button>
                    </PopoverTrigger>
                    <Portal>
                      <PopoverContent w="595px" zIndex={1400}>
                        <PopoverBody>
                          <DateRangePicker
                            onChange={(item: any) =>
                              setPeriodDraft(item.selection)
                            }
                            showSelectionPreview
                            moveRangeOnFirstSelection={false}
                            ranges={[periodDraft]}
                            firstDayOfWeek={1}
                            direction="horizontal"
                            scroll={{ enabled: true }}
                            className={`date-range-picker ${colorMode}`}
                          />
                          <Flex gap={2} mt={3}>
                            <Button size="sm" variant="brand" onClick={onClose}>
                              Done
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                clearDraftDates();
                                onClose();
                              }}
                            >
                              Clear
                            </Button>
                          </Flex>
                        </PopoverBody>
                      </PopoverContent>
                    </Portal>
                  </>
                )}
              </Popover>
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
                isLoading={isLoading}
              >
                Search
              </Button>
              <Button
                onClick={resetFilters}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
                isDisabled={!hasFilterInput}
              >
                Reset
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                leftIcon={<DownloadIcon />}
                size="md"
                isLoading={exporting}
                loadingText="Preparing…"
                isDisabled={count === 0}
              >
                Download CSV
              </Button>
            </Flex>
          </Stack>
        </Box>

        <TableContainer overflowX="auto">
          <Table variant="simple" color="gray.500">
            <Thead>
              <Tr>
                {tableHeaders.map((header, index) => (
                  <Th
                    key={header}
                    pe="10px"
                    borderColor={borderColor}
                    color="gray.400"
                    {...(index === 0 ? stickyCellProps : {})}
                  >
                    {header}
                  </Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {isLoading ? (
                <Tr>
                  <Td colSpan={tableHeaders.length} textAlign="center">
                    <Flex align="center" justify="center" mt="20px" mb="20px">
                      <Spinner size="sm" mr="10px" /> Loading referrals...
                    </Flex>
                  </Td>
                </Tr>
              ) : rows.length === 0 ? (
                <Tr>
                  <Td
                    colSpan={tableHeaders.length}
                    textAlign="center"
                    color="gray.400"
                  >
                    No referrals found.
                  </Td>
                </Tr>
              ) : (
                rows.map((r) => {
                  const badge = STATUS_BADGE[r.status];
                  return (
                    <Tr key={r.id} role="group" _hover={{ bg: rowHoverBg }}>
                      <Td
                        borderColor={borderColor}
                        {...stickyCellProps}
                        _groupHover={{ bg: rowHoverBg }}
                      >
                        <VStack align="flex-start" spacing={0}>
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                          >
                            {r.broker_name}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {r.broker_email}
                          </Text>
                        </VStack>
                      </Td>
                      {isAdmin && (
                        <Td borderColor={borderColor}>
                          <Text color={textColor} fontSize="sm">
                            {r.branch_name}
                          </Text>
                        </Td>
                      )}
                      <Td borderColor={borderColor}>
                        <Text color={textColor} fontSize="sm">
                          {r.market || '—'}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {badge && (
                          <Badge
                            colorScheme={badge.color}
                            borderRadius="full"
                            px={2}
                          >
                            {badge.label}
                          </Badge>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm" color="gray.500">
                          {r.assigned_to_name || r.assigned_to_email || '—'}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {r.jira_ticket_key ? (
                          <Link
                            href={`${JIRA_BASE_URL}/browse/${r.jira_ticket_key}`}
                            isExternal
                            fontSize="sm"
                            fontWeight="500"
                            color="brand.500"
                          >
                            {r.jira_ticket_key}{' '}
                            <Icon as={ExternalLinkIcon} boxSize={3} />
                          </Link>
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        {SIO_BADGE[r.sio_status] ? (
                          <Tooltip label={SIO_BADGE[r.sio_status].hint}>
                            <Badge
                              colorScheme={SIO_BADGE[r.sio_status].color}
                              borderRadius="full"
                              px={2}
                            >
                              {SIO_BADGE[r.sio_status].label}
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        {!r.post_bind_review_required ? (
                          <Text fontSize="sm" color="gray.400">
                            —
                          </Text>
                        ) : (
                          <Badge
                            colorScheme={
                              r.post_bind_reviewed ? 'green' : 'yellow'
                            }
                            borderRadius="full"
                            px={2}
                          >
                            {r.post_bind_reviewed ? 'Reviewed' : 'Pending'}
                          </Badge>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        {r.post_bind_reviewed_by_name ? (
                          <Tooltip label={r.post_bind_reviewed_by_email ?? ''}>
                            <Text fontSize="sm" color="gray.500">
                              {formatReviewedBy(
                                r.post_bind_reviewed_by_name,
                                r.post_bind_reviewed_at,
                              )}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text fontSize="sm" color="gray.400">
                            —
                          </Text>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text
                          fontSize="sm"
                          color="gray.500"
                          whiteSpace="nowrap"
                        >
                          {formatDateTime(r.submitted_at)}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text color={textColor} fontSize="sm">
                          {formatTurnaround(r.turnaround_minutes)}
                        </Text>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {!isLoading && count > 0 && (
          <Flex
            px="20px"
            pt="20px"
            justify="space-between"
            align="center"
            gap={4}
          >
            <Text fontSize="sm" color="gray.500">
              Showing {page * pageSize + 1}–
              {Math.min((page + 1) * pageSize, count)} of {count}
            </Text>
            <Paginate
              page={page}
              count={count}
              pageSize={pageSize}
              onSetPage={setPage}
            />
          </Flex>
        )}
      </Card>
    </BaraFeatureGuard>
  );
};

export default BaraReferralReportView;
